import * as XLSX from 'xlsx-js-style';

// Brand-aligned palette (matches DESIGN.md).
// Amber is the only chromatic colour — used in title bar + header chrome.
// Everything else is warm neutral / semantic.
export const PALETTE = {
  amber600: 'EA580C',
  amber100: 'FFEDD5',
  amber050: 'FFF7ED',
  ink:      '1A1410',
  ink2:     '3D2E22',
  ink3:     '6B5A4A',
  line:     'E8DFD3',
  lineSoft: 'F1EBE0',
  bg:       'FFFCF7',
  bg2:      'FAF4EA',
  success:  'D1FAE5',
  successInk: '047857',
  danger:   'FEE2E2',
  dangerInk: 'B91C1C',
  warn:     'FEF3C7',
  warnInk:  '92400E',
};

const FONT = 'Geist';
const MONO = 'Geist Mono';

const border = (color = PALETTE.line) => ({
  top:    { style: 'thin', color: { rgb: color } },
  bottom: { style: 'thin', color: { rgb: color } },
  left:   { style: 'thin', color: { rgb: color } },
  right:  { style: 'thin', color: { rgb: color } },
});

export const STYLE = {
  titleBar: {
    font: { name: FONT, sz: 18, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: PALETTE.amber600 } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
    border: border(PALETTE.amber600),
  },
  subtitle: {
    font: { name: MONO, sz: 10, bold: true, color: { rgb: PALETTE.ink2 } },
    fill: { fgColor: { rgb: PALETTE.amber050 } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
    border: border(PALETTE.line),
  },
  headerPrimary: {
    font: { name: MONO, sz: 10, bold: true, color: { rgb: PALETTE.ink } },
    fill: { fgColor: { rgb: PALETTE.amber100 } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: border(PALETTE.amber600),
  },
  headerSecondary: {
    font: { name: MONO, sz: 9, bold: true, color: { rgb: PALETTE.ink2 } },
    fill: { fgColor: { rgb: PALETTE.bg2 } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: border(PALETTE.line),
  },
  bodyText: {
    font: { name: FONT, sz: 10, color: { rgb: PALETTE.ink } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
    border: border(PALETTE.lineSoft),
  },
  bodyTextCenter: {
    font: { name: FONT, sz: 10, color: { rgb: PALETTE.ink } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(PALETTE.lineSoft),
  },
  bodyNum: {
    font: { name: MONO, sz: 10, color: { rgb: PALETTE.ink } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(PALETTE.lineSoft),
  },
  bodyId: {
    font: { name: MONO, sz: 9, color: { rgb: PALETTE.ink2 } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(PALETTE.lineSoft),
  },
  zebra: { fgColor: { rgb: PALETTE.bg2 } },
  statusDone: {
    font: { name: MONO, sz: 9, bold: true, color: { rgb: PALETTE.successInk } },
    fill: { fgColor: { rgb: PALETTE.success } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(PALETTE.lineSoft),
  },
  statusNotDone: {
    font: { name: MONO, sz: 9, bold: true, color: { rgb: PALETTE.dangerInk } },
    fill: { fgColor: { rgb: PALETTE.danger } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(PALETTE.lineSoft),
  },
  statusWeekOff: {
    font: { name: MONO, sz: 9, bold: true, color: { rgb: PALETTE.warnInk } },
    fill: { fgColor: { rgb: PALETTE.warn } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: border(PALETTE.lineSoft),
  },
  totalsRow: {
    font: { name: MONO, sz: 10, bold: true, color: { rgb: PALETTE.ink } },
    fill: { fgColor: { rgb: PALETTE.amber100 } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: { ...border(PALETTE.amber600), top: { style: 'medium', color: { rgb: PALETTE.amber600 } } },
  },
  footer: {
    font: { name: MONO, sz: 8, italic: true, color: { rgb: PALETTE.ink3 } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
  },
};

export function statusStyle(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'done') return STYLE.statusDone;
  if (s === 'not done') return STYLE.statusNotDone;
  if (s === 'week off') return STYLE.statusWeekOff;
  return STYLE.bodyTextCenter;
}

// Build a worksheet from a 2D array of cell objects {v, t?, s?, z?, f?}
// or plain values. Plain values get default body style applied.
export function buildSheet({ aoa, cols, merges, freeze, autoFilter, sheetName }) {
  const ws = XLSX.utils.aoa_to_sheet(
    aoa.map(row => row.map(cell => {
      if (cell == null) return '';
      if (typeof cell === 'object' && 'v' in cell) return cell;
      return cell;
    }))
  );

  // Apply per-cell styles for object cells.
  for (let R = 0; R < aoa.length; R++) {
    for (let C = 0; C < aoa[R].length; C++) {
      const cell = aoa[R][C];
      if (cell == null || typeof cell !== 'object' || !('v' in cell)) continue;
      const ref = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[ref]) ws[ref] = { t: cell.t || 's', v: cell.v };
      if (cell.s) ws[ref].s = cell.s;
      if (cell.z) ws[ref].z = cell.z;
      if (cell.t) ws[ref].t = cell.t;
      if (cell.f) ws[ref].f = cell.f;
    }
  }

  if (cols) ws['!cols'] = cols;
  if (merges) ws['!merges'] = merges;
  if (freeze) ws['!freeze'] = freeze;
  if (freeze) {
    ws['!views'] = [{
      state: 'frozen',
      xSplit: freeze.xSplit || 0,
      ySplit: freeze.ySplit || 0,
      topLeftCell: XLSX.utils.encode_cell({ r: freeze.ySplit || 0, c: freeze.xSplit || 0 }),
      activePane: 'bottomRight',
    }];
  }
  if (autoFilter) ws['!autofilter'] = { ref: autoFilter };

  // Sensible default print + sheet name.
  ws['!margins'] = { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };
  if (sheetName) ws['!sheet'] = sheetName;
  return ws;
}

export function downloadWorkbook(wb, filename) {
  XLSX.writeFile(wb, filename, { cellStyles: true });
}

export function makeWorkbook() {
  return XLSX.utils.book_new();
}

export function appendSheet(wb, ws, name) {
  // Sheet names: max 31 chars, no [ ] : * ? / \
  const clean = String(name).replace(/[[\]:*?/\\]/g, ' ').slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, clean);
}

// Build the standard 3-row title block: amber bar, subtitle, blank spacer.
export function titleBlock({ title, subtitle, spanCols }) {
  const titleRow = [{ v: title, s: STYLE.titleBar }];
  for (let i = 1; i < spanCols; i++) titleRow.push({ v: '', s: STYLE.titleBar });
  const subRow = [{ v: subtitle, s: STYLE.subtitle }];
  for (let i = 1; i < spanCols; i++) subRow.push({ v: '', s: STYLE.subtitle });
  return {
    rows: [titleRow, subRow, Array(spanCols).fill('')],
    merges: [
      { s: { r: 0, c: 0 }, e: { r: 0, c: spanCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: spanCols - 1 } },
    ],
    rowHeights: [{ hpt: 26 }, { hpt: 18 }, { hpt: 6 }],
  };
}

export function footerBlock({ generatedBy, contextLine, spanCols }) {
  const ts = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const line1 = `Generated · ${ts} IST · by ${generatedBy || 'dashboard user'}`;
  const line2 = contextLine || '';
  const r1 = [{ v: line1, s: STYLE.footer }];
  for (let i = 1; i < spanCols; i++) r1.push({ v: '', s: STYLE.footer });
  const r2 = [{ v: line2, s: STYLE.footer }];
  for (let i = 1; i < spanCols; i++) r2.push({ v: '', s: STYLE.footer });
  return {
    rows: [Array(spanCols).fill(''), r1, r2],
    merges: (baseRow) => [
      { s: { r: baseRow + 1, c: 0 }, e: { r: baseRow + 1, c: spanCols - 1 } },
      { s: { r: baseRow + 2, c: 0 }, e: { r: baseRow + 2, c: spanCols - 1 } },
    ],
  };
}

// Zebra-stripe data rows in-place (skips rows with explicit fills already).
export function zebraStripe(aoa, dataStartRow) {
  for (let r = dataStartRow; r < aoa.length; r++) {
    if ((r - dataStartRow) % 2 === 1) {
      for (let c = 0; c < aoa[r].length; c++) {
        const cell = aoa[r][c];
        if (cell && typeof cell === 'object' && cell.s && !cell.s.fill) {
          cell.s = { ...cell.s, fill: STYLE.zebra };
        }
      }
    }
  }
}
